/**
 * Converts an ISO 3166-1 alpha-2 country code to its corresponding flag emoji
 * using Unicode regional indicator symbols (e.g., "US" → 🇺🇸).
 */
export const getCountryFlag = (countryCode: string): string => {
  if (!countryCode || countryCode.length !== 2) return '';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

import moment from 'moment-timezone';

const buildTimezoneToCountryMap = (): Record<string, string> => {
  const map: Record<string, string> = {};
  for (const country of moment.tz.countries()) {
    for (const zone of moment.tz.zonesForCountry(country)) {
      if (!map[zone]) {
        map[zone] = country;
      }
    }
  }
  return map;
};

let timezoneToCountryCache: Record<string, string> | null = null;

const getTimezoneToCountryMap = (): Record<string, string> => {
  if (!timezoneToCountryCache) {
    timezoneToCountryCache = buildTimezoneToCountryMap();
  }
  return timezoneToCountryCache;
};

/**
 * Derives an ISO country code from an IANA timezone string.
 *
 * - Always returns a country when one can be determined, even if the timezone
 *   is shared by multiple countries (the primary / first-registered country wins).
 * - Handles deprecated timezone aliases (e.g. "Asia/Calcutta" → "IN") by
 *   normalising to the canonical name via moment.tz.zone().
 * - Returns null only for country-agnostic pseudo-timezones ("Etc/UTC", "UTC",
 *   "GMT", etc.) that are not associated with any specific country.
 */
export const getCountryFromTimezone = (
  timezone: string | undefined | null
): string | null => {
  if (!timezone) return null;

  const map = getTimezoneToCountryMap();

  if (map[timezone]) return map[timezone];

  const zone = moment.tz.zone(timezone);
  if (zone?.name && map[zone.name]) return map[zone.name];

  return null;
};
